import { useEffect, useState } from "react";
import { mapString, isString } from "utils/common";
import {
	getInputsWithoutAdditionalFields,
	getInitialInput,
	getInitialInputs,
	getAdditionalFields,
	getSelectedOptionAdditionalFieldsLinkedTo,
	initializeLinkedFieldChild,
	getFilePath,
	focusFirstInvalidField,
} from "utils/form";

const useForm = (
	initialInputs = {},
	initialInlineErrors = {},
	initialFormGroups = {}
) => {
	const [formInputs, setFormInputs] = useState({
		...(getInitialInputs(initialInputs, initialFormGroups) || {}),
		files: [],
	});
	const [inlineErrors, setInlineErrors] = useState(initialInlineErrors);
	const [additionalFields, setAdditionalFields] = useState({});
	const [linkedFields, setLinkedFields] = useState({});

	useEffect(() => {
		for (const inputName in formInputs) {
			if (inputName === "files") continue;
			if (initialInputs[inputName]?.childrenLinkedTo?.length) {
				checkLinkedFields(inputName, formInputs[inputName]);
			}
			checkAdditionalFields(initialInputs[inputName], formInputs[inputName]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleError = (
		message,
		name,
		isValid,
		setHasError,
		validateExistence
	) => {
		if (isValid) {
			removeInputError(name, validateExistence);
		} else {
			setInlineErrors((currentValue) => {
				return { ...currentValue, [name]: message };
			});
		}
		setHasError && setHasError(!isValid);
	};

	const setFiles = async (file, name, isMultipleFile, maxSize = 2) => {
		const fileIndex = getFileIndex(name);
		const maxFileSize = 1024 * 1024 * maxSize;
		if ((!file || file?.size > maxFileSize) && !isMultipleFile) {
			formInputs[name] &&
				setFormInputs((currentValue) => {
					delete currentValue[name];
					return {
						...currentValue,
						files: [
							...currentValue.files.slice(0, fileIndex),
							...currentValue.files.slice(fileIndex + 1),
						],
					};
				});
			removeInputError(name);
		}

		let newFiles;
		try {
			const path = await getFilePath(file);
			const newFile = {
				[name]: name,
				filename: file.name,
				size: file.size,
				path,
				file: file,
			};
			if (fileIndex > -1) {
				// to replace the uploaded file
				newFiles = [
					...formInputs.files.slice(0, fileIndex),
					newFile,
					...formInputs.files.slice(fileIndex + 1),
				];
			} else {
				newFiles = [newFile];
			}
			setFormInputs((currentState) => ({
				...currentState,
				[name]: {
					...currentState[name],
					name: file.name,
					size: file.size,
					type: file.type,
				},
				files:
					isMultipleFile && fileIndex === -1
						? [...currentState.files, newFile]
						: newFiles,
			}));
		} catch (error) {
			console.error(error);
		}
	};

	const removeFile = (name) => {
		const fileIndex = formInputs.files?.findIndex(
			(item) => item[name] === name
		);
		if (fileIndex > -1) {
			setFormInputs((currentState) => ({
				...currentState,
				files: [
					...currentState.files.slice(0, fileIndex),
					...currentState.files.slice(fileIndex + 1),
				],
			}));
		}
	};

	const removePreviousAdditionalFields = (selectedInput) => {
		if (
			Array.isArray(additionalFields[selectedInput.name]?.fields) &&
			additionalFields[selectedInput.name].fields.length
		) {
			additionalFields[selectedInput.name].fields.forEach((field) => {
				if (field.formFields) {
					field.formFields?.forEach((fieldGroup) => {
						removeInputValue(fieldGroup.name);
						removeInputError(fieldGroup.name);
						formInputs.files.length && removeFile(fieldGroup.name);
					});
				} else {
					removeInputValue(field.name);
					removeInputError(field.name);
					formInputs.files.length && removeFile(field.name);
				}
			});
		}
	};

	const addAdditionalFields = (selectedInput, value) => {
		const selectedFieldAdditionalFieldsLinkedTo =
			getSelectedOptionAdditionalFieldsLinkedTo(selectedInput, value);
		const currentAdditionalFields =
			selectedFieldAdditionalFieldsLinkedTo.displays;

		if (
			Array.isArray(currentAdditionalFields) &&
			currentAdditionalFields.length
		) {
			let transformedAdditionalFields = [];
			currentAdditionalFields.forEach((additionalFieldName) => {
				if (initialFormGroups[additionalFieldName]) {
					transformedAdditionalFields = transformedAdditionalFields.concat({
						id: initialFormGroups[additionalFieldName]?.id,
						title: initialFormGroups[additionalFieldName]?.title,
						formFields: initialFormGroups[additionalFieldName]?.formFields
							?.filter((field) => initialInputs[field]?.name)
							.map((groupFieldName) => {
								const groupField = getInitialInput(
									initialInputs[groupFieldName]
								);
								if (groupField.value) {
									addInputValue(groupFieldName, groupField.value);
								}
								return groupField;
							}),
					});
				} else if (initialInputs[additionalFieldName]?.name) {
					const input = getInitialInput(initialInputs[additionalFieldName]);
					if (input?.value) {
						addInputValue(additionalFieldName, input.value);
					}
					transformedAdditionalFields =
						transformedAdditionalFields.concat(input);
				}
			});
			setAdditionalFields({
				...additionalFields,
				[selectedInput.name]: {
					...additionalFields[selectedInput.name],
					title: selectedFieldAdditionalFieldsLinkedTo.displaysTitle,
					fields: transformedAdditionalFields,
				},
			});
		} else {
			setAdditionalFields({
				...additionalFields,
				[selectedInput.name]: {
					...additionalFields[selectedInput.name],
					title: "",
					fields: [],
				},
			});
		}
	};

	const checkAdditionalFields = (selectedInput = {}, value) => {
		if (!selectedInput.hasAdditionalFields || !selectedInput.name) return;
		removePreviousAdditionalFields(selectedInput);
		addAdditionalFields(selectedInput, value);
	};

	const handleChange = (e) => {
		const { value, name, type, files, checked } = e.target || {};
		switch (type) {
			case "number":
				addInputValue(name, parseInt(value));
				break;
			case "file":
				const file = files[0];
				const isMultipleFile =
					document.querySelectorAll("input[type=file]").length > 1;
				setFiles(file, name, isMultipleFile);
				break;
			case "checkbox":
				addInputValue(name, checked);
				break;
			case "nestedSelect":
				addInputValue(name, value);
				removeInputError(name);
				break;
			case "date":
				if (value) {
					addInputValue(name, value);
				} else {
					removeInputValue(name, false);
				}
				break;
			default:
				addInputValue(name, value);
		}

		if (initialInputs[name]?.childrenLinkedTo?.length) {
			checkLinkedFields(name, value);
		}
		checkAdditionalFields(initialInputs[name], value);
	};

	const handleMultipleFiles = (
		id,
		required,
		notEmptyMessage,
		maxFileSizeErrorMessage,
		accept,
		acceptErrorMessage,
		isMultipleFile = false,
		maxSize = 2,
		files,
		setHasError
	) => {
		const count = files.length;
		for (let index = 0; index < count; index++) {
			const name = `${id}${index}.`;
			validateInputFile(
				name,
				files[index],
				required,
				notEmptyMessage,
				maxFileSizeErrorMessage,
				accept,
				acceptErrorMessage,
				maxSize,
				setHasError
			);
			setFiles(files[index], name, isMultipleFile, maxSize);
		}
	};

	const checkLinkedFields = (parentName, parentValue) => {
		if (initialInputs[parentName]?.childrenLinkedTo?.length) {
			initialInputs[parentName].childrenLinkedTo?.forEach((field) => {
				const linkedChild = initializeLinkedFieldChild(
					initialInputs[field],
					initialInputs[parentName],
					parentValue
				);
				setLinkedFields((currentState) => ({
					...currentState,
					[field]: { ...currentState[field], ...linkedChild },
				}));
				if (linkedChild.value) {
					addInputValue(field, linkedChild.value);
				} else if (formInputs[field]) {
					removeInputValue(field);
				}
				checkAdditionalFields(initialInputs[field], linkedChild.value);
				checkLinkedFields(field, linkedChild.value);
			});
		}
	};

	const validateInput = (
		name,
		value,
		required,
		notEmptyMessage,
		pattern,
		patternNotMatchMessage,
		setHasError,
		isEmailInput = false,
		validEmailDomains = [],
		notAllowedDomainMessage
	) => {
		const isRendered = document.getElementById(name);
		if (isRendered) {
			const validateEmpty = required && notEmptyMessage;
			const validateRegex = pattern && patternNotMatchMessage;
			const isValid = !!value;
			const domain = isString(value) ? value.split("@")[1] : "";

			isEmailInput &&
				validEmailDomains &&
				notAllowedDomainMessage &&
				handleError(
					notAllowedDomainMessage,
					name,
					!value?.includes("+") && validEmailDomains.includes(`${domain}`),
					setHasError
				);
			validateEmpty &&
				(!validateRegex || !isValid) &&
				handleError(notEmptyMessage, name, !!value, setHasError);
			validateRegex &&
				(!validateEmpty || isValid) &&
				handleError(
					patternNotMatchMessage,
					name,
					!!value?.match(pattern),
					setHasError
				);
		} else {
			removeInputError(name, false);
		}
	};

	const validateInputDate = (name, isValid, invalidFormatErrorMessage) => {
		handleError(invalidFormatErrorMessage, name, isValid, null, false);
	};

	const validateInputFile = (
		name,
		file,
		required,
		notEmptyMessage,
		maxFileSizeErrorMessage,
		accept,
		acceptErrorMessage,
		maxSize = 2,
		setHasError
	) => {
		const maxFileSize = 1024 * 1024 * maxSize; // Default 2MB
		const defaultFileType = "*";
		const filename = file?.name;
		let isValid = file?.size <= maxFileSize;
		let isFormatValid = false;
		const fileType = file?.type
			? file?.type
			: filename?.substr(filename.lastIndexOf(".") + 1);

		if (!fileType?.includes("/")) {
			isFormatValid = accept?.includes(file?.type);
		} else {
			accept?.forEach((format) => {
				if (format?.includes(fileType)) {
					isFormatValid = true;
				}
			});
		}

		removeFilesErrors();
		acceptErrorMessage = mapString(
			acceptErrorMessage,
			Array.isArray(accept) ? accept?.join(", ") : accept
		);
		required &&
			!file &&
			handleError(notEmptyMessage, name, isValid, setHasError, false);
		file &&
			!isValid &&
			handleError(maxFileSizeErrorMessage, name, isValid, setHasError, false);
		acceptErrorMessage &&
			accept &&
			isValid &&
			handleError(
				acceptErrorMessage,
				name,
				accept === defaultFileType || isFormatValid,
				setHasError,
				false
			);
	};

	const validateFormInputs = (inputs) => {
		for (const inputName in inputs) {
			if (!inputs[inputName]?.name) continue;
			const {
				notEmptyMessage,
				patternNotMatchMessage,
				maxFileSizeErrorMessage,
				acceptErrorMessage,
				required,
				pattern,
				type,
				accept,
				name,
				maxSize,
				notAllowedDomainMessage,
				allowedEmailDomains,
			} = inputs[inputName];

			const value = formInputs[name];
			if (type === "file") {
				validateInputFile(
					name,
					value,
					required,
					notEmptyMessage,
					maxFileSizeErrorMessage,
					accept,
					acceptErrorMessage,
					maxSize
				);
			} else {
				validateInput(
					name,
					value,
					required,
					notEmptyMessage,
					pattern,
					patternNotMatchMessage,
					null,
					name === "emailAddress",
					allowedEmailDomains,
					notAllowedDomainMessage
				);
			}
		}
	};

	const validateForm = () => {
		validateFormInputs(getInputsWithoutAdditionalFields(initialInputs));
		validateFormInputs(getAdditionalFields(additionalFields));
	};

	const removeInputError = (name, validateExistence = true) => {
		if (!validateExistence || (validateExistence && inlineErrors[name])) {
			setInlineErrors((currentValue) => {
				delete currentValue[name];
				return { ...currentValue };
			});
		}
	};

	const removeFilesErrors = () => {
		const uploadFileKey = "uploadFile";
		setInlineErrors((currentState) => {
			Object.keys(currentState).forEach((key) => {
				formInputs.files.forEach((file) => {
					if (!file[key] && key.includes(uploadFileKey)) {
						delete currentState[key];
					}
				});
			});
			return currentState;
		});
	};

	const addInputValue = (name, value) => {
		setFormInputs((currentState) => ({
			...currentState,
			[name]: value,
		}));
	};

	const getFileIndex = (name) => {
		return formInputs.files?.findIndex((item) => item[name] === name);
	};

	const removeInputValue = (name, validateExistence = true) => {
		if (!validateExistence || (validateExistence && formInputs[name])) {
			setFormInputs((currentValue) => {
				delete currentValue[name];
				return { ...currentValue };
			});
		}
	};

	return {
		formInputs,
		inlineErrors,
		additionalFields,
		linkedFields,
		addInputValue,
		handleChange,
		removeInputValue,
		validateInput,
		validateInputFile,
		handleMultipleFiles,
		validateInputDate,
		validateForm,
		focusFirstInvalidField,
		removeFile,
	};
};

export default useForm;
